import { useEffect, useRef, useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { consumer } from "@/lib/cable"
import { useAuthStore } from "@/stores/auth-store"

interface EntityUpdateMessage {
  type: "entity_update"
  entity_type: string
  entity_id: string
  action: "created" | "updated" | "deleted"
  data: Record<string, unknown>
  timestamp: string
}

/**
 * Maps entity types to their TanStack Query cache keys.
 */
const ENTITY_QUERY_KEY_MAP: Record<string, string[]> = {
  Company: ["companies"],
  Contact: ["contacts"],
  Deal: ["deals"],
  Task: ["tasks"],
  Invoice: ["invoices"],
  Quote: ["quotes"],
  Activity: ["activities"],
  Document: ["documents"],
  Notification: ["notifications"],
}

/**
 * Subscribes to the EntityUpdatesChannel via ActionCable.
 * Invalidates relevant TanStack Query caches when entities
 * are created, updated, or deleted by other users.
 *
 * @param entityTypes - Optional filter: only react to updates for these entity types
 * @param onUpdate - Optional callback for custom handling
 */
export function useEntityUpdates(
  entityTypes?: string[],
  onUpdate?: (msg: EntityUpdateMessage) => void
) {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()
  const subscriptionRef = useRef<ReturnType<typeof consumer.subscriptions.create> | null>(null)

  const handleReceived = useCallback(
    (data: EntityUpdateMessage) => {
      // Filter by entity type if specified
      if (entityTypes && !entityTypes.includes(data.entity_type)) return

      // Invalidate relevant query caches
      const queryKeys = ENTITY_QUERY_KEY_MAP[data.entity_type]
      if (queryKeys) {
        queryClient.invalidateQueries({ queryKey: queryKeys })
      }

      // Call custom handler if provided
      onUpdate?.(data)
    },
    [queryClient, entityTypes, onUpdate]
  )

  useEffect(() => {
    if (!isAuthenticated) return

    subscriptionRef.current = consumer.subscriptions.create(
      { channel: "EntityUpdatesChannel" },
      {
        received(data: unknown) {
          handleReceived(data as EntityUpdateMessage)
        },
      }
    )

    return () => {
      subscriptionRef.current?.unsubscribe()
      subscriptionRef.current = null
    }
  }, [isAuthenticated, handleReceived])
}
